/**
 * BehaviorAnalyticsDashboard.jsx
 * User behavior analytics dashboard for owner view
 * 
 * Location: src/components/owner/BehaviorAnalyticsDashboard.jsx
 */

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const BehaviorAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('flows');
  const [data, setData] = useState(null);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      const getBehaviorAnalytics = httpsCallable(functions, 'getBehaviorAnalytics');
      const result = await getBehaviorAnalytics({ timeRange });
      
      if (result.data.success) {
        setData(result.data);
      } else {
        setError(result.data.error || 'Failed to fetch analytics');
      }
    } catch (err) {
      console.error('Error fetching behavior analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f1f5f9',
      padding: '24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '16px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#243665',
      margin: '0 0 4px 0',
    },
    subtitle: {
      fontSize: '14px',
      color: '#64748b',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
    },
    select: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      fontSize: '14px',
      backgroundColor: 'white',
    },
    btn: {
      padding: '8px 16px',
      backgroundColor: '#243665',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '16px',
      marginBottom: '24px',
    },
    summaryCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      textAlign: 'center',
    },
    summaryValue: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#243665',
      marginBottom: '4px',
    },
    summaryLabel: {
      fontSize: '12px',
      color: '#64748b',
      marginBottom: '8px',
    },
    trend: {
      fontSize: '11px',
      padding: '2px 8px',
      borderRadius: '4px',
      display: 'inline-block',
    },
    tabs: {
      display: 'flex',
      gap: '4px',
      marginBottom: '24px',
      backgroundColor: 'white',
      padding: '4px',
      borderRadius: '10px',
      width: 'fit-content',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    tab: {
      padding: '10px 20px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#64748b',
    },
    tabActive: {
      backgroundColor: '#243665',
      color: 'white',
    },
    section: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '16px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      fontSize: '11px',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase',
      borderBottom: '2px solid #e5e7eb',
      backgroundColor: '#f8fafc',
    },
    td: {
      padding: '12px',
      fontSize: '13px',
      color: '#374151',
      borderBottom: '1px solid #f1f5f9',
    },
    flowCard: {
      padding: '16px',
      backgroundColor: '#f8fafc',
      borderRadius: '10px',
      border: '2px solid #e2e8f0',
      marginBottom: '12px',
    },
    flowHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
    },
    flowName: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1e293b',
    },
    flowBadge: {
      fontSize: '10px',
      padding: '3px 8px',
      borderRadius: '4px',
      fontWeight: '600',
    },
    flowPath: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginBottom: '12px',
    },
    flowStep: {
      fontSize: '11px',
      padding: '4px 8px',
      backgroundColor: '#e5e7eb',
      borderRadius: '4px',
      color: '#374151',
    },
    flowStats: {
      display: 'flex',
      gap: '16px',
      fontSize: '12px',
      color: '#64748b',
    },
    insights: {
      backgroundColor: '#fffbeb',
      border: '1px solid #fde68a',
      borderRadius: '12px',
      padding: '20px',
      marginTop: '24px',
    },
    insightItem: {
      display: 'flex',
      gap: '12px',
      fontSize: '13px',
      color: '#78350f',
      marginBottom: '10px',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '400px',
      backgroundColor: 'white',
      borderRadius: '12px',
    },
    errorContainer: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '12px',
      padding: '20px',
      textAlign: 'center',
      color: '#dc2626',
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#64748b',
    },
  };

  // Render loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>
            <div style={{ fontSize: '24px', marginBottom: '12px' }}>📊</div>
            <div style={{ color: '#64748b' }}>Loading analytics...</div>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={{ fontSize: '24px', marginBottom: '12px' }}>⚠️</div>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>Error loading analytics</div>
          <div style={{ fontSize: '13px' }}>{error}</div>
          <button 
            style={{ ...styles.btn, marginTop: '16px' }}
            onClick={fetchAnalytics}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { summary, pageAnalytics, actionAnalytics, discoveredFlows, liveSessions, insights } = data || {};

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📊 User Behavior Analytics</h1>
          <p style={styles.subtitle}>Track every interaction • Discover patterns • Optimize flows</p>
        </div>
        <div style={styles.headerActions}>
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)} 
            style={styles.select}
          >
            <option value="today">Today</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button style={styles.btn} onClick={fetchAnalytics}>↻ Refresh</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{summary?.totalSessions?.toLocaleString() || 0}</div>
          <div style={styles.summaryLabel}>Total Sessions</div>
          <span style={{ ...styles.trend, backgroundColor: '#dcfce7', color: '#16a34a' }}>↑ Active</span>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{summary?.activeUsers || 0}</div>
          <div style={styles.summaryLabel}>Active Users</div>
          <span style={{ ...styles.trend, backgroundColor: '#dcfce7', color: '#16a34a' }}>↑ Tracking</span>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{summary?.avgPagesPerSession || 0}</div>
          <div style={styles.summaryLabel}>Avg Pages/Session</div>
          <span style={{ ...styles.trend, backgroundColor: '#f3f4f6', color: '#374151' }}>—</span>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{summary?.avgSessionTime || '0s'}</div>
          <div style={styles.summaryLabel}>Avg Session Time</div>
          <span style={{ ...styles.trend, backgroundColor: '#f3f4f6', color: '#374151' }}>—</span>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{summary?.liveNow || 0}</div>
          <div style={styles.summaryLabel}>Live Now</div>
          <span style={{ ...styles.trend, backgroundColor: '#dbeafe', color: '#2563eb' }}>● Live</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: 'flows', icon: '🔀', label: 'User Flows' },
          { id: 'pages', icon: '📄', label: 'Pages' },
          { id: 'actions', icon: '⚡', label: 'Actions' },
          { id: 'live', icon: '🔴', label: 'Live' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Flows Tab */}
      {activeTab === 'flows' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>🤖 Auto-Discovered Patterns</h2>
          {discoveredFlows && discoveredFlows.length > 0 ? (
            discoveredFlows.map((flow, idx) => (
              <div 
                key={idx} 
                style={{
                  ...styles.flowCard,
                  borderColor: flow.type === 'problem' ? '#fecaca' : 
                               flow.type === 'frequent' ? '#dbeafe' : '#e2e8f0',
                  backgroundColor: flow.type === 'problem' ? '#fef2f2' : 
                                   flow.type === 'frequent' ? '#eff6ff' : '#f8fafc',
                }}
              >
                <div style={styles.flowHeader}>
                  <span style={styles.flowName}>{flow.name}</span>
                  <span style={{
                    ...styles.flowBadge,
                    backgroundColor: flow.type === 'frequent' ? '#dbeafe' : 
                                     flow.type === 'problem' ? '#fee2e2' : '#f3f4f6',
                    color: flow.type === 'frequent' ? '#1e40af' : 
                           flow.type === 'problem' ? '#dc2626' : '#374151',
                  }}>
                    {flow.type === 'frequent' ? '🔥 Frequent' : 
                     flow.type === 'problem' ? '⚠️ Problem' : 'Moderate'}
                  </span>
                </div>
                <div style={styles.flowPath}>
                  {flow.path.map((step, i) => (
                    <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={styles.flowStep}>{step}</span>
                      {i < flow.path.length - 1 && <span style={{ color: '#9ca3af' }}>→</span>}
                    </span>
                  ))}
                </div>
                <div style={styles.flowStats}>
                  <span>👥 {flow.sessions} sessions</span>
                  <span>⏱️ {flow.avgTime}</span>
                </div>
              </div>
            ))
          ) : (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>No patterns discovered yet</div>
              <div>User flow patterns will appear here once more data is collected.</div>
            </div>
          )}
        </div>
      )}

      {/* Pages Tab */}
      {activeTab === 'pages' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📄 Page Analytics</h2>
          {pageAnalytics && pageAnalytics.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Page</th>
                  <th style={styles.th}>Views</th>
                  <th style={styles.th}>Unique Users</th>
                  <th style={styles.th}>Exit Rate</th>
                  <th style={styles.th}>Top Next Pages</th>
                </tr>
              </thead>
              <tbody>
                {pageAnalytics.map((page, idx) => (
                  <tr key={idx}>
                    <td style={{ ...styles.td, fontWeight: '600' }}>{page.page}</td>
                    <td style={styles.td}>{page.views.toLocaleString()}</td>
                    <td style={styles.td}>{page.uniqueUsers}</td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: parseInt(page.exitRate) > 25 ? '#fee2e2' : '#f3f4f6',
                        color: parseInt(page.exitRate) > 25 ? '#dc2626' : '#374151',
                      }}>{page.exitRate}</span>
                    </td>
                    <td style={{ ...styles.td, fontSize: '11px', color: '#64748b' }}>
                      {page.nextPages.join(' • ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
              <div>No page data available yet.</div>
            </div>
          )}
        </div>
      )}

      {/* Actions Tab */}
      {activeTab === 'actions' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>⚡ Action Analytics</h2>
          {actionAnalytics && actionAnalytics.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Action</th>
                  <th style={styles.th}>Count</th>
                  <th style={styles.th}>Unique Users</th>
                  <th style={styles.th}>Triggered From</th>
                  <th style={styles.th}>Peak Hour</th>
                </tr>
              </thead>
              <tbody>
                {actionAnalytics.map((action, idx) => (
                  <tr key={idx}>
                    <td style={{ ...styles.td, fontWeight: '600' }}>
                      {action.action.includes('created') ? '✅ ' : 
                       action.action.includes('edited') ? '✏️ ' : 
                       action.action.includes('deleted') ? '🗑️ ' : '⚡ '}
                      {action.action}
                    </td>
                    <td style={styles.td}>{action.count}</td>
                    <td style={styles.td}>{action.uniqueUsers}</td>
                    <td style={{ ...styles.td, fontSize: '11px', color: '#64748b' }}>
                      {action.fromPages.join(' • ')}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                      }}>{action.peakHour}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
              <div>No action data available yet.</div>
            </div>
          )}
        </div>
      )}

      {/* Live Tab */}
      {activeTab === 'live' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              backgroundColor: '#22c55e',
              borderRadius: '50%',
              marginRight: '8px',
            }}></span>
            Live Sessions ({liveSessions?.length || 0} active)
          </h2>
          {liveSessions && liveSessions.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Club</th>
                  <th style={styles.th}>Current Page</th>
                  <th style={styles.th}>Duration</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {liveSessions.map((session, idx) => (
                  <tr key={idx}>
                    <td style={styles.td}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: session.status === 'active' ? '#dcfce7' : '#fef3c7',
                        color: session.status === 'active' ? '#16a34a' : '#d97706',
                      }}>
                        <span style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: session.status === 'active' ? '#22c55e' : '#f59e0b',
                        }}></span>
                        {session.status === 'active' ? 'Active' : 'Idle'}
                      </span>
                    </td>
                    <td style={styles.td}>{session.user}</td>
                    <td style={styles.td}>{session.club}</td>
                    <td style={{ ...styles.td, fontWeight: '600' }}>{session.currentPage}</td>
                    <td style={styles.td}>{session.duration}</td>
                    <td style={styles.td}>{session.actions} actions</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>💤</div>
              <div>No active sessions right now.</div>
            </div>
          )}
        </div>
      )}

      {/* AI Insights */}
      {insights && insights.length > 0 && (
        <div style={styles.insights}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#92400e', marginBottom: '16px' }}>
            💡 AI Insights
          </h2>
          {insights.map((insight, idx) => (
            <div key={idx} style={styles.insightItem}>
              <span>{insight.icon}</span>
              <span>
                <strong>{insight.title}:</strong> {insight.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Meta info */}
      {data?.meta && (
        <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '12px', color: '#9ca3af' }}>
          Analyzed {data.meta.eventsAnalyzed.toLocaleString()} events • Last updated: {new Date(data.meta.generatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default BehaviorAnalyticsDashboard;
